import type { Occurrence } from './mtxbudget';

/** One movement of money on one day, seen at the bank or planned. */
export type CalendarEvent = {
  id: string;
  /** 'YYYY-MM-DD'. */
  date: string;
  name: string;
  kind:
    | 'transaction'
    | 'pay'
    | 'charge'
    | 'oneoff'
    | 'investment'
    | 'income'
    | 'debt';
  /** Signed cents: positive for money in, negative for money out. */
  amount: number;
  /** True once it went through the bank; false while it is only planned. */
  done: boolean;
  /** A payment switched off by hand: shown, not counted. */
  skipped?: boolean;
  /** The card it is charged to: it leaves no cash that day. */
  card?: string;
  account?: string;
  /** The hours a pay to come is computed from. */
  hours?: [number, number];
  /** What « Prévisions » can change, and under which id. */
  edit?: { kind: 'depense' | 'entree' | 'investissement'; id: string };
};

export type CalendarTransaction = {
  id: string;
  date: string;
  amount: number;
  name: string;
  account?: string;
  isTransfer?: boolean;
  isStartingBalance?: boolean;
};

/** What the calendar reads of a pay of the walk. */
export type CalendarStep = {
  payday: string;
  status: 'received' | 'worked' | 'open' | 'future';
  hours: [number, number];
  net: number;
  charges: (Occurrence & { skipped: boolean; cardId?: string })[];
  incomes: { id: string; name: string; amount: number; date: string }[];
  debts: { id: string; name: string; extra: number }[];
};

const INVESTMENT = 'investissement/';
const ORDER: CalendarEvent['kind'][] = [
  'transaction',
  'pay',
  'income',
  'charge',
  'oneoff',
  'investment',
  'debt',
];

/**
 * Every movement of money, day by day: up to today what the bank shows,
 * from today on what the walk through the pays plans. A transfer between
 * one's own accounts is neither money in nor money out.
 */
export function calendarEvents({
  today,
  transactions,
  steps,
}: {
  today: string;
  transactions: readonly CalendarTransaction[];
  steps: readonly CalendarStep[];
}): CalendarEvent[] {
  const events: CalendarEvent[] = transactions
    .filter(
      transaction =>
        !transaction.isTransfer &&
        !transaction.isStartingBalance &&
        transaction.amount !== 0,
    )
    .map(transaction => ({
      id: `transaction/${transaction.id}`,
      date: transaction.date,
      name: transaction.name,
      kind: 'transaction' as const,
      amount: transaction.amount,
      done: true,
      account: transaction.account,
    }));

  for (const step of steps) {
    const cards = new Map(step.debts.map(debt => [debt.id, debt.name]));
    if (step.status !== 'received' && step.payday >= today) {
      events.push({
        id: `pay/${step.payday}`,
        date: step.payday,
        name: '',
        kind: 'pay',
        amount: step.net,
        done: false,
        hours: step.hours,
      });
      for (const debt of step.debts) {
        if (debt.extra > 0) {
          events.push({
            id: `debt/${debt.id}/${step.payday}`,
            date: step.payday,
            name: debt.name,
            kind: 'debt',
            amount: -debt.extra,
            done: false,
          });
        }
      }
    }
    for (const charge of step.charges) {
      if (charge.date < today) {
        continue;
      }
      const investment = charge.id.startsWith(INVESTMENT);
      const oneOff = charge.recurring === false;
      events.push({
        id: `charge/${charge.id}`,
        date: charge.date,
        name: charge.name,
        kind: investment ? 'investment' : oneOff ? 'oneoff' : 'charge',
        amount: charge.amount,
        done: false,
        ...(charge.skipped ? { skipped: true } : {}),
        ...(charge.cardId ? { card: cards.get(charge.cardId) } : {}),
        ...(investment
          ? {
              edit: {
                kind: 'investissement' as const,
                id: charge.id.slice(INVESTMENT.length),
              },
            }
          : oneOff && charge.scheduleId
            ? { edit: { kind: 'depense' as const, id: charge.scheduleId } }
            : {}),
      });
    }
    for (const income of step.incomes) {
      if (income.date >= today) {
        events.push({
          id: `income/${income.id}`,
          date: income.date,
          name: income.name,
          kind: 'income',
          amount: income.amount,
          done: false,
          edit: { kind: 'entree', id: income.id },
        });
      }
    }
  }

  return events.sort(
    (a, b) =>
      a.date.localeCompare(b.date) ||
      ORDER.indexOf(a.kind) - ORDER.indexOf(b.kind),
  );
}

/** Switched off, or put on a card: no cash moves that day. */
function movesCash(event: CalendarEvent): boolean {
  return !event.skipped && event.card === undefined;
}

/** Cash in and cash out of each day that has any, in positive cents. */
export function dayTotals(
  events: readonly CalendarEvent[],
): Map<string, { in: number; out: number }> {
  const totals = new Map<string, { in: number; out: number }>();
  for (const event of events) {
    const total = totals.get(event.date) ?? { in: 0, out: 0 };
    if (movesCash(event)) {
      if (event.amount > 0) {
        total.in += event.amount;
      } else {
        total.out -= event.amount;
      }
    }
    totals.set(event.date, total);
  }
  return totals;
}

function iso(date: Date): string {
  return date.toISOString().slice(0, 10);
}

/** The full weeks, Sunday first, that hold a month given as 'YYYY-MM'. */
export function monthWeeks(month: string): string[][] {
  const [year, index] = month.split('-').map(Number);
  const first = new Date(Date.UTC(year, index - 1, 1));
  const last = new Date(Date.UTC(year, index, 0));
  const day = new Date(first);
  day.setUTCDate(day.getUTCDate() - day.getUTCDay());
  const weeks: string[][] = [];
  while (day <= last) {
    const week: string[] = [];
    for (let i = 0; i < 7; i++) {
      week.push(iso(day));
      day.setUTCDate(day.getUTCDate() + 1);
    }
    weeks.push(week);
  }
  return weeks;
}

/** The seven days, Sunday first, of the week a day is in. */
export function weekOf(date: string): string[] {
  const day = new Date(`${date}T00:00:00Z`);
  day.setUTCDate(day.getUTCDate() - day.getUTCDay());
  return Array.from({ length: 7 }, () => {
    const current = iso(day);
    day.setUTCDate(day.getUTCDate() + 1);
    return current;
  });
}

export type MonthTotal = {
  /** 'YYYY-MM'. */
  month: string;
  /** Cash in and out of the month, in positive cents. */
  in: number;
  out: number;
  /** The part of each that already went through the bank. */
  doneIn: number;
  doneOut: number;
};

/** The twelve months of a year given as 'YYYY', with what each brings and costs. */
export function monthTotals(
  events: readonly CalendarEvent[],
  year: string,
): MonthTotal[] {
  const months: MonthTotal[] = Array.from({ length: 12 }, (_, index) => ({
    month: `${year}-${String(index + 1).padStart(2, '0')}`,
    in: 0,
    out: 0,
    doneIn: 0,
    doneOut: 0,
  }));
  for (const event of events) {
    const total = months.find(month => event.date.startsWith(month.month));
    if (!total || !movesCash(event)) {
      continue;
    }
    if (event.amount > 0) {
      total.in += event.amount;
      total.doneIn += event.done ? event.amount : 0;
    } else {
      total.out -= event.amount;
      total.doneOut -= event.done ? event.amount : 0;
    }
  }
  return months;
}
